import { useState, useReducer, useEffect, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useContactService } from '../services/contactService';
import { usePortalUserManagementService } from '../services/portalUserManagementService';
import { useSnackbar } from '../../../shared/hooks/useSnackbar';
import { RpcError } from '../../../shared/api/rpcClient';
import type { ContactWithUser } from '../../../types';

// Query keys for the user-management feature's cacheable server reads. Keyed by
// customer so a customer switch is a distinct cache entry.
export const userManagementKeys = {
  contacts: (customerId: string) => ['user-management', 'contacts', customerId] as const,
};

interface MetadataFormState {
  contact_types: string[];
  role: string;
  name: string;
  phone_number: string;
}

type MetadataFormAction =
  | { type: 'SET_TYPES'; payload: string[] }
  | { type: 'SET_ROLE'; payload: string }
  | { type: 'SET_NAME'; payload: string }
  | { type: 'SET_PHONE'; payload: string }
  | { type: 'LOAD'; payload: MetadataFormState }
  | { type: 'RESET' };

const initialFormState: MetadataFormState = {
  contact_types: [],
  role: '',
  name: '',
  phone_number: '',
};

function metadataFormReducer(state: MetadataFormState, action: MetadataFormAction): MetadataFormState {
  switch (action.type) {
    case 'SET_TYPES': return { ...state, contact_types: action.payload };
    case 'SET_ROLE': return { ...state, role: action.payload };
    case 'SET_NAME': return { ...state, name: action.payload };
    case 'SET_PHONE': return { ...state, phone_number: action.payload };
    case 'LOAD': return action.payload;
    case 'RESET': return initialFormState;
  }
}

export function useContactsTab(isActive: boolean, customerId: string) {
  const service = useContactService();
  const portalService = usePortalUserManagementService();
  const { snackbar, showSnackbar, closeSnackbar } = useSnackbar();

  const [form, dispatchForm] = useReducer(metadataFormReducer, initialFormState);
  const [selectedContact, setSelectedContact] = useState<ContactWithUser | null>(null);
  const [saving, setSaving] = useState(false);

  const query = useQuery({
    queryKey: userManagementKeys.contacts(customerId),
    queryFn: () => service.listContacts(customerId),
    enabled: isActive,
  });

  const contacts = (query.data ?? []) as ContactWithUser[];
  const loading = query.isLoading;

  const { refetch, error } = query;

  const loadContacts = useCallback(async (): Promise<ContactWithUser[]> => {
    const result = await refetch();
    return (result.data ?? []) as ContactWithUser[];
  }, [refetch]);

  // Surface a load failure as a snackbar.
  useEffect(() => {
    if (error) {
      const msg = error instanceof RpcError ? error.message : 'Failed to load contacts';
      showSnackbar(msg, 'error');
    }
  }, [error, showSnackbar]);

  // Reset the selection when the tab opens OR the customer changes — otherwise a
  // customer switch leaves the previous customer's contact selected against the
  // new (query-refetched) list.
  useEffect(() => {
    if (isActive) {
      setSelectedContact(null);
      dispatchForm({ type: 'RESET' });
    }
  }, [isActive, customerId]);

  const selectContact = useCallback((contact: ContactWithUser) => {
    setSelectedContact(contact);
    dispatchForm({
      type: 'LOAD',
      payload: {
        contact_types: contact.contact_types,
        role: contact.role,
        name: contact.name,
        phone_number: contact.phone_number ?? '',
      },
    });
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedContact(null);
    dispatchForm({ type: 'RESET' });
  }, []);

  const handleSaveMetadata = useCallback(async () => {
    if (!selectedContact) return;

    setSaving(true);
    try {
      await service.updateContactMetadata(customerId, selectedContact.username, {
        contact_types: form.contact_types,
        role: form.role,
      });
      await portalService.updatePortalUser(customerId, selectedContact.username, {
        name: form.name,
        phone_number: form.phone_number,
      });
      showSnackbar('Contact updated', 'success');
      const refreshed = await loadContacts();
      const updated = refreshed.find((c) => c.username === selectedContact.username);
      if (updated) {
        setSelectedContact(updated);
      }
    } catch (err: unknown) {
      const msg = err instanceof RpcError ? err.message : 'Failed to update contact';
      showSnackbar(msg, 'error');
    } finally {
      setSaving(false);
    }
  }, [selectedContact, form, service, portalService, customerId, showSnackbar, loadContacts]);

  const handleReorderOperational = useCallback(async (orderedUsernames: string[]) => {
    try {
      await service.reorderOperationalContacts(customerId, orderedUsernames);
      showSnackbar('Call chain updated', 'success');
      await loadContacts();
    } catch (err: unknown) {
      const msg = err instanceof RpcError ? err.message : 'Failed to reorder contacts';
      showSnackbar(msg, 'error');
    }
  }, [service, customerId, showSnackbar, loadContacts]);

  const handleUpdateVocabulary = useCallback(async (contactTypes: string[]) => {
    try {
      await service.updateCustomerContactTypes(customerId, contactTypes);
      showSnackbar('Contact types updated', 'success');
      await loadContacts();
    } catch (err: unknown) {
      const msg = err instanceof RpcError ? err.message : 'Failed to update contact types';
      showSnackbar(msg, 'error');
    }
  }, [service, customerId, showSnackbar, loadContacts]);

  return {
    form,
    dispatchForm,
    contacts,
    selectedContact,
    selectContact,
    clearSelection,
    loadContacts,
    loading,
    saving,
    snackbar,
    closeSnackbar,
    handleSaveMetadata,
    handleReorderOperational,
    handleUpdateVocabulary,
  };
}

export type { MetadataFormState, MetadataFormAction };
